import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Button, ButtonText, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import {
  ChevronDown, Mic, MicOff, Video, VideoOff,
} from 'lucide-react-native';
import { Platform, Pressable, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLocalParticipant } from '@livekit/components-react';
import type { LucideIcon } from 'lucide-react-native';

import { attachPreviewStream } from '@/common/components/meeting/attachPreviewStream';
import { isSelfServiceRun } from '@/common/utils/oralExam/runConduct';
import { useCarriedJoinSettings } from '@/student/module/hooks/useCarriedJoinSettings';

import { AudioLevelMeter } from './AudioLevelMeter';
import type { LobbyStepProps } from './types';
import { useMicAudioLevel } from './useMicAudioLevel';

type MediaDeviceInfo = { deviceId: string; label: string };

const PREVIEW_VIDEO_STYLE = {
  width: '100%' as const,
  height: 200,
  objectFit: 'cover' as const,
  display: 'block' as const,
  transform: 'scaleX(-1)' as const,
};

function siteHostForPermissionHelp(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return 'this site';
}

/**
 * A single mic/cam control: a toggle button (on/off) fused with an integrated
 * device-picker dropdown — the familiar videoconferencing control. Replaces the
 * earlier separate toggle row + two device-pill rows so the step fits the fold.
 */
function LobbyMediaControl({
  iconOn,
  iconOff,
  on,
  onToggle,
  devices,
  selectedId,
  onSelect,
  toggleTestID,
  pillTestIDPrefix,
  toggleA11yLabel,
}: {
  iconOn: LucideIcon;
  iconOff: LucideIcon;
  on: boolean;
  onToggle: () => void;
  devices: MediaDeviceInfo[];
  selectedId: string | undefined;
  onSelect: (deviceId: string) => void;
  toggleTestID: string;
  pillTestIDPrefix: string;
  toggleA11yLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const effectiveId = selectedId ?? devices[0]?.deviceId;
  const selected = devices.find((d) => d.deviceId === effectiveId) ?? devices[0];

  return (
    <Box position="relative" zIndex={open ? 20 : 1}>
      <HStack
        alignItems="center"
        borderRadius="$full"
        borderWidth={1}
        borderColor="$borderLight"
        bg="$bgApp"
        overflow="hidden"
      >
        <Pressable onPress={onToggle} testID={toggleTestID} accessibilityRole="button" accessibilityLabel={toggleA11yLabel}>
          <Box px="$3" py="$2" bg={on ? '$bgApp' : '$statusErrorSubtle'}>
            <Icon as={on ? iconOn : iconOff} size="sm" color="$textHeading" />
          </Box>
        </Pressable>
        {devices.length > 0 ? (
          <Pressable onPress={() => setOpen((v) => !v)} testID={`${pillTestIDPrefix}-select`} accessibilityRole="button">
            <HStack
              alignItems="center"
              space="xs"
              px="$3"
              py="$2"
              borderLeftWidth={1}
              borderLeftColor="$borderLight"
            >
              <Box maxWidth={150}>
                <Text size="xs" color="$textBody" numberOfLines={1}>
                  {selected?.label}
                </Text>
              </Box>
              <Icon as={ChevronDown} size="xs" color="$textSubtle" />
            </HStack>
          </Pressable>
        ) : null}
      </HStack>

      {open && devices.length > 0 ? (
        <VStack
          position="absolute"
          top="100%"
          left={0}
          mt="$1"
          minWidth={220}
          bg="$bgCard"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$md"
          overflow="hidden"
          softShadow="1"
          zIndex={30}
        >
          {devices.map((device) => {
            const isSelected = device.deviceId === effectiveId;
            return (
              <Pressable
                key={device.deviceId}
                onPress={() => { onSelect(device.deviceId); setOpen(false); }}
                testID={`${pillTestIDPrefix}-pill-${device.deviceId}`}
                accessibilityRole="button"
              >
                <Box px="$3" py="$2" bg={isSelected ? '$bgBrandSubtle' : '$bgCard'}>
                  <Text size="sm" color={isSelected ? '$textHeading' : '$textBody'} numberOfLines={1}>
                    {device.label}
                  </Text>
                </Box>
              </Pressable>
            );
          })}
        </VStack>
      ) : null}
    </Box>
  );
}

/** Preview element gets video only, so the mic check cannot play back through the speakers. */
function videoOnlyPreview(stream: MediaStream): MediaStream {
  const videoTracks = stream.getVideoTracks();
  try {
    const preview = new MediaStream(videoTracks);
    if (preview.getAudioTracks().length === 0) return preview;
  } catch {
    // A test double is not a real MediaStreamTrack. Fall through to a video-only view.
  }
  return {
    getVideoTracks: () => videoTracks,
    getAudioTracks: () => [],
    getTracks: () => videoTracks,
  } as MediaStream;
}

/**
 * Lobby step — camera and mic check. The meter is local. A graded pre-flight
 * publishes the camera only; the microphone stays off until the exam room.
 * A dry run is already in its own exam room, so that check may publish the mic.
 */
export function LobbyDeviceStep({ onComplete, onLeave, runState }: LobbyStepProps) {
  const { t } = useTranslation();
  const { markMediaGranted } = useCarriedJoinSettings();
  const { localParticipant } = useLocalParticipant();
  const isWeb = Platform.OS === 'web';
  const siteHost = useMemo(() => siteHostForPermissionHelp(), []);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const skipVideoEffectOnceRef = useRef(false);
  const skipAudioEffectOnceRef = useRef(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);

  const [mediaGranted, setMediaGranted] = useState(false);
  const [requestInFlight, setRequestInFlight] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDeviceId, setAudioDeviceId] = useState<string | undefined>();
  const [videoDeviceId, setVideoDeviceId] = useState<string | undefined>();
  const [mediaBlocked, setMediaBlocked] = useState(false);
  const [showMediaGuidance, setShowMediaGuidance] = useState(false);

  const audioLevel = useMicAudioLevel({ audioStream, enabled: micOn && !mediaBlocked && mediaGranted });

  const stopVideoPreview = useCallback(() => {
    videoStreamRef.current?.getTracks().forEach((track) => track.stop());
    videoStreamRef.current = null;
    setCameraStream(null);
  }, []);

  const stopAudioPreview = useCallback(() => {
    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    audioStreamRef.current = null;
    setAudioStream(null);
  }, []);

  const refreshDevices = useCallback(async () => {
    if (!isWeb || !globalThis.navigator?.mediaDevices?.enumerateDevices) return;
    const all = await globalThis.navigator.mediaDevices.enumerateDevices();
    setAudioDevices(
      all.filter((d) => d.kind === 'audioinput').map((d) => ({
        deviceId: d.deviceId,
        label: d.label || t('oralExam.lobby.device.defaultMic'),
      })),
    );
    setVideoDevices(
      all.filter((d) => d.kind === 'videoinput').map((d) => ({
        deviceId: d.deviceId,
        label: d.label || t('oralExam.lobby.device.defaultCamera'),
      })),
    );
  }, [isWeb, t]);

  const publishMicrophone = isSelfServiceRun(runState);

  const assignPreviewStreams = useCallback((stream: MediaStream) => {
    const preview = videoOnlyPreview(stream);
    videoStreamRef.current = preview;
    audioStreamRef.current = stream;
    setCameraStream(preview);
    setAudioStream(stream);
  }, []);

  const publishToRoom = useCallback(async (): Promise<boolean> => {
    try {
      await localParticipant.setCameraEnabled(true);
      // A graded check uses a local stream for the meter. The shared room may
      // already be publishing the microphone from connect; turn that off.
      await localParticipant.setMicrophoneEnabled(publishMicrophone);
      return true;
    } catch {
      return false;
    }
  }, [localParticipant, publishMicrophone]);

  const requestMediaAccess = useCallback(async (): Promise<boolean> => {
    if (!isWeb) {
      setMediaGranted(true);
      setMicOn(true);
      setCamOn(true);
      markMediaGranted();
      return true;
    }
    setRequestInFlight(true);
    setMediaBlocked(false);
    setShowMediaGuidance(false);
    try {
      stopVideoPreview();
      stopAudioPreview();
      const stream = await globalThis.navigator.mediaDevices.getUserMedia({
        audio: audioDeviceId ? { deviceId: audioDeviceId } : true,
        video: videoDeviceId ? { deviceId: videoDeviceId } : true,
      });
      assignPreviewStreams(stream);
      setMicOn(true);
      setCamOn(true);
      await refreshDevices();
      const published = await publishToRoom();
      if (!published) {
        setMediaBlocked(true);
        return false;
      }
      skipVideoEffectOnceRef.current = true;
      skipAudioEffectOnceRef.current = true;
      setMediaGranted(true);
      markMediaGranted();
      return true;
    } catch {
      setMediaBlocked(true);
      return false;
    } finally {
      setRequestInFlight(false);
    }
  }, [
    assignPreviewStreams,
    audioDeviceId,
    isWeb,
    publishToRoom,
    refreshDevices,
    stopAudioPreview,
    stopVideoPreview,
    markMediaGranted,
    videoDeviceId,
  ]);

  const handleEnableMedia = useCallback(() => {
    requestMediaAccess().catch(() => undefined);
  }, [requestMediaAccess]);

  const handleRetryMedia = useCallback(() => {
    requestMediaAccess().catch(() => undefined);
  }, [requestMediaAccess]);

  useEffect(() => {
    if (!isWeb || !mediaGranted) return undefined;
    if (skipVideoEffectOnceRef.current) {
      skipVideoEffectOnceRef.current = false;
      return undefined;
    }
    if (!camOn) {
      stopVideoPreview();
      return undefined;
    }
    let cancelled = false;
    const start = async () => {
      try {
        stopVideoPreview();
        const vidStream = await globalThis.navigator.mediaDevices.getUserMedia({
          audio: false,
          video: videoDeviceId ? { deviceId: videoDeviceId } : true,
        });
        if (cancelled) {
          vidStream.getTracks().forEach((track) => track.stop());
          return;
        }
        videoStreamRef.current = vidStream;
        setCameraStream(vidStream);
        if (videoRef.current) {
          videoRef.current.srcObject = vidStream;
          videoRef.current.play().catch(() => undefined);
        }
        await refreshDevices();
      } catch {
        // Preview unavailable — student can still continue (non-blocking step).
      }
    };
    start();
    return () => { cancelled = true; };
  }, [camOn, isWeb, mediaGranted, refreshDevices, stopVideoPreview, videoDeviceId]);

  useEffect(() => {
    if (!isWeb || !mediaGranted) return undefined;
    if (skipAudioEffectOnceRef.current) {
      skipAudioEffectOnceRef.current = false;
      return undefined;
    }
    if (!micOn) {
      stopAudioPreview();
      return undefined;
    }
    let cancelled = false;
    const start = async () => {
      try {
        stopAudioPreview();
        const audStream = await globalThis.navigator.mediaDevices.getUserMedia({
          audio: audioDeviceId ? { deviceId: audioDeviceId } : true,
          video: false,
        });
        if (cancelled) {
          audStream.getTracks().forEach((track) => track.stop());
          return;
        }
        audioStreamRef.current = audStream;
        setAudioStream(audStream);
        await refreshDevices();
      } catch {
        setMediaBlocked(true);
      }
    };
    start();
    return () => { cancelled = true; };
  }, [audioDeviceId, isWeb, mediaGranted, micOn, refreshDevices, stopAudioPreview]);

  useEffect(() => () => {
    stopVideoPreview();
    stopAudioPreview();
  }, [stopAudioPreview, stopVideoPreview]);

  const previewVideoRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (!node || !cameraStream || !mediaGranted || mediaBlocked || !camOn) return;
    attachPreviewStream(node, cameraStream);
  }, [camOn, cameraStream, mediaBlocked, mediaGranted]);

  useLayoutEffect(() => {
    if (!isWeb || !mediaGranted || mediaBlocked || !camOn || !cameraStream) return;
    attachPreviewStream(videoRef.current, cameraStream);
  }, [camOn, cameraStream, isWeb, mediaBlocked, mediaGranted]);

  const blockedRecovery = mediaBlocked ? (
    <VStack
      space="md"
      alignItems="center"
      maxWidth={520}
      width="100%"
      testID="oral-exam-lobby-mic-blocked"
    >
      <Text size="md" color="$statusError" textAlign="center">
        {t('oralExam.lobby.device.blockedLead')}
      </Text>
      <Text size="sm" color="$textBody" textAlign="center">
        {t('oralExam.lobby.device.blockedFix')}
      </Text>
      {showMediaGuidance ? (
        <VStack space="sm" width="100%">
          <Text size="sm" color="$textSubtle" textAlign="center">
            {t('oralExam.mediaBlockedHelpChrome', { host: siteHost })}
          </Text>
          <Text size="sm" color="$textSubtle" textAlign="center">
            {t('oralExam.mediaBlockedHelpSafari', { host: siteHost })}
          </Text>
        </VStack>
      ) : null}
      <Button size="sm" onPress={handleRetryMedia}>
        <ButtonText>{t('oralExam.lobby.device.tryAgain')}</ButtonText>
      </Button>
      <Button
        size="sm"
        variant="outline"
        onPress={() => setShowMediaGuidance(true)}
        testID="oral-exam-lobby-media-more-help"
      >
        <ButtonText>{t('oralExam.lobby.device.moreHelp')}</ButtonText>
      </Button>
      {onLeave ? (
        <Button
          size="sm"
          variant="outline"
          onPress={onLeave}
          testID="oral-exam-lobby-mic-blocked-leave"
        >
          <ButtonText>{t('oralExam.lobby.device.leaveExam')}</ButtonText>
        </Button>
      ) : null}
      <Text size="sm" color="$textSubtle" textAlign="center">
        {t('oralExam.lobby.device.blockedConsequence')}
      </Text>
    </VStack>
  ) : null;

  const primingBody = !mediaGranted && !mediaBlocked ? (
    <VStack space="md" alignItems="center" maxWidth={520} width="100%">
      <VStack space="xs" alignItems="center">
        <Text size="lg" fontWeight="semibold" color="$textHeading" textAlign="center">
          {t('oralExam.lobby.device.title')}
        </Text>
        <Text size="sm" color="$textBody" textAlign="center">
          {t('oralExam.lobby.device.primingBody')}
        </Text>
        <Text size="sm" color="$textSubtle" textAlign="center">
          {t('oralExam.lobby.device.privacyNote')}
        </Text>
      </VStack>
      <Button
        size="md"
        onPress={handleEnableMedia}
        isDisabled={requestInFlight}
        testID="oral-exam-lobby-device-enable"
        width="100%"
        maxWidth={320}
      >
        <ButtonText>
          {requestInFlight
            ? t('oralExam.lobby.device.waiting')
            : t('oralExam.lobby.device.enableButton')}
        </ButtonText>
      </Button>
    </VStack>
  ) : null;

  const grantedBody = mediaGranted && !mediaBlocked ? (
    <>
      <VStack space="xs" alignItems="center" maxWidth={520}>
        <Text size="lg" fontWeight="semibold" color="$textHeading" textAlign="center">
          {t('oralExam.lobby.device.grantedTitle')}
        </Text>
        <Text size="sm" color="$textBody" textAlign="center">
          {t('oralExam.lobby.device.privacyNoteGranted')}
        </Text>
      </VStack>

      <Box
        width="100%"
        maxWidth={520}
        borderRadius="$lg"
        overflow="hidden"
        borderWidth={1}
        borderColor="$borderLight"
        bg="$bgSubtle"
      >
        {isWeb && camOn && cameraStream ? (
          <Box bg="$bgDark" testID="oral-exam-lobby-preview-video">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video ref={previewVideoRef as never} muted playsInline style={PREVIEW_VIDEO_STYLE} />
          </Box>
        ) : (
          <Box height={200} alignItems="center" justifyContent="center" px="$4" bg="$bgDark">
            <Text size="sm" color="$textSubtle" textAlign="center">
              {camOn
                ? t('oralExam.lobby.device.previewPlaceholder')
                : t('oralExam.lobby.device.cameraOff')}
            </Text>
          </Box>
        )}
      </Box>

      <Box width="100%" maxWidth={520}>
        <AudioLevelMeter level={audioLevel} />
      </Box>

      <Box
        width="100%"
        maxWidth={520}
        position="relative"
        zIndex={10}
        alignItems="center"
        testID="oral-exam-lobby-device-controls"
      >
        <HStack space="md" alignItems="flex-start" justifyContent="center" flexWrap="wrap">
          <LobbyMediaControl
            iconOn={Mic}
            iconOff={MicOff}
            on={micOn}
            onToggle={() => setMicOn((v) => !v)}
            devices={isWeb ? audioDevices : []}
            selectedId={audioDeviceId}
            onSelect={setAudioDeviceId}
            toggleTestID="oral-exam-lobby-mic-toggle"
            pillTestIDPrefix="oral-exam-lobby-audio"
            toggleA11yLabel={t('oralExam.lobby.device.micPillLabel')}
          />
          <LobbyMediaControl
            iconOn={Video}
            iconOff={VideoOff}
            on={camOn}
            onToggle={() => setCamOn((v) => !v)}
            devices={isWeb ? videoDevices : []}
            selectedId={videoDeviceId}
            onSelect={setVideoDeviceId}
            toggleTestID="oral-exam-lobby-cam-toggle"
            pillTestIDPrefix="oral-exam-lobby-video"
            toggleA11yLabel={t('oralExam.lobby.device.cameraPillLabel')}
          />
        </HStack>
      </Box>

      <Box width="100%" maxWidth={320} position="relative" zIndex={0} alignItems="center">
        <Button
          size="md"
          onPress={onComplete}
          testID="oral-exam-lobby-device-continue"
          width="100%"
        >
          <ButtonText>{t('oralExam.lobby.device.continue')}</ButtonText>
        </Button>
      </Box>
    </>
  ) : null;

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ flexGrow: 1 }}
      showsVerticalScrollIndicator={false}
    >
      <VStack space="md" p="$4" alignItems="center" testID="oral-exam-lobby-device-step">
        <HStack
          alignSelf="stretch"
          justifyContent="flex-end"
          alignItems="center"
          space="xs"
          testID="oral-exam-rec-indicator"
          accessibilityLabel={t('oralExam.lobby.device.recArmedA11y')}
          accessibilityValue={{ text: 'armed' }}
        >
          <Box w={8} h={8} borderRadius="$full" bg="$textDisabled" />
          <Text size="xs" color="$textSubtle" fontWeight="medium">
            {t('oralExam.lobby.device.recArmed')}
          </Text>
        </HStack>

        {blockedRecovery}
        {primingBody}
        {grantedBody}
      </VStack>
    </ScrollView>
  );
}
